import { useCallback, useState, type ChangeEvent } from "react";
import AddIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  styled,
  Typography,
} from "@mui/material";
import Button from "@components/Button/Button";
import FormDivider from "@components/FormDivider/FormDivider";
import FormItem from "@components/FormItem/FormItem";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import Input from "@components/Input/Input";
import type { TruckGatepassFormData } from "@features/gatepass/schema/truckGatepassFormSchema";
import { formatOnChangeEmiratesIDNumber } from "@features/port-security/components/utils";
import { checkGatepassSecurityClearance } from "@services/securityClearanceApi";
import { runFieldNavigation } from "@telemetry/runFieldNavigation";
import { runInquiry } from "@telemetry/runInquiry";
import { paths } from "@utils/navigation";
import { t } from "i18next";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import SecurityClearanceStatusMessage from "../NormalGatepass/SecurityClearanceStatusMessage";
import { createScIdValidators, extractScErrorDetail, resolveScSearchField } from "../utils";
import type { TruckDriverFormData } from "./types";

interface DriverDetailsProps {
  index: number;
  onFieldChange: (key: string, value: unknown) => void;
  disableAddDriver?: boolean;
  autoPopulateFirstDriverAcrossVehicles?: boolean;
}

const TRANSLATION_KEY = "gatepass";
const GATEPASS_SERVICE = "bff-gatepass";
const DRIVER_SECURITY_CLEARANCE_CHECK_INQUIRY_CONTEXT = {
  module: "gatepass",
  screen: "Truck Gatepass Driver Details",
  action: "check_driver_security_clearance",
  entityType: "security_clearance",
};
const DRIVER_SECURITY_CLEARANCE_FIELD_NAVIGATION_CONTEXT = {
  module: "gatepass",
  screen: "Truck Gatepass Driver Details",
  action: "populate_driver_security_clearance",
  fromField: "driver.documentId",
  toField: "driver.securityClearance",
  entityType: "security_clearance",
};

const RemoveButton = styled(Button)(() => ({
  "color": "#333333",
  "backgroundColor": "#F1F2F4",
  "&:hover": {
    backgroundColor: "#F1F2F4",
  },
}));

const scValidityErrorSx = {
  ml: 0,
  fontWeight: 500,
  fontSize: 12,
  marginY: "4px",
  color: "error.main",
} as const;

const renderScValidityError = (message: string, testId: string) => (
  <FormHelperText error sx={scValidityErrorSx} data-testid={testId}>
    {String(message)}
  </FormHelperText>
);

const formatEmiratesIdValue = (value: string) => {
  const event = {
    target: { value },
  } as unknown as ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;

  formatOnChangeEmiratesIDNumber(event);
  return event.target.value;
};

const DriverDetails = ({
  index,
  onFieldChange,
  disableAddDriver = false,
  autoPopulateFirstDriverAcrossVehicles = false,
}: DriverDetailsProps) => {
  const fottDisplayOptions = [
    { label: t("common.yes"), value: t("yes") },
    { label: t("common.no"), value: t("no") },
  ];
  const formData = useFormContext<TruckGatepassFormData>();
  const { fields, append, remove } = useFieldArray({
    control: formData.control,
    name: `vehicles.${index}.drivers`,
  });

  const validators = createScIdValidators(TRANSLATION_KEY, t);
  const [isCheckingByRow, setIsCheckingByRow] = useState<Record<number, boolean>>({});
  const [lastSearchFieldByRow, setLastSearchFieldByRow] = useState<
    Record<number, "passport" | "emirates" | null>
  >({});
  const [scStatusByRow, setScStatusByRow] = useState<Record<number, string>>({});
  const [scSerialByRow, setScSerialByRow] = useState<Record<number, string>>({});
  const [lookupErrorByIdx, setLookupErrorByIdx] = useState<Record<number, string>>({});

  const syncDriversToStore = () => {
    onFieldChange("vehicles", formData.getValues("vehicles"));
  };

  const setLookupError = (driverIndex: number, message: string) => {
    setLookupErrorByIdx((prev) => ({ ...prev, [driverIndex]: message }));
  };

  const clearLookupError = (driverIndex: number) => {
    setLookupErrorByIdx((prev) => {
      const next = { ...prev };
      delete next[driverIndex];
      return next;
    });
  };

  const resetRowScState = useCallback((driverIndex: number) => {
    setIsCheckingByRow((prev) => {
      if (!prev[driverIndex]) return prev;
      return { ...prev, [driverIndex]: false };
    });
    setLastSearchFieldByRow((prev) => {
      if (!prev[driverIndex]) return prev;
      return { ...prev, [driverIndex]: null };
    });
    setScStatusByRow((prev) => {
      if (!prev[driverIndex]) return prev;
      return { ...prev, [driverIndex]: "" };
    });
    setScSerialByRow((prev) => {
      if (!prev[driverIndex]) return prev;
      return { ...prev, [driverIndex]: "" };
    });
    clearLookupError(driverIndex);
  }, []);

  const remapStateAfterRemoval = useCallback((removedIndex: number) => {
    const remap = <T,>(state: Record<number, T>) => {
      const next: Record<number, T> = {};
      Object.entries(state).forEach(([key, value]) => {
        const idx = Number(key);
        if (idx < removedIndex) next[idx] = value;
        if (idx > removedIndex) next[idx - 1] = value;
      });
      return next;
    };

    setIsCheckingByRow((prev) => remap(prev));
    setLastSearchFieldByRow((prev) => remap(prev));
    setScStatusByRow((prev) => remap(prev));
    setScSerialByRow((prev) => remap(prev));
    setLookupErrorByIdx((prev) => remap(prev));
  }, []);

  const handleDriverIdChange = useCallback(
    (driverIndex: number, fieldName: "passportNumber" | "emiratesIdNumber", rawValue: unknown) => {
      const normalizedValue = String(rawValue ?? "").trim();
      const oppositeFieldName =
        fieldName === "passportNumber" ? "emiratesIdNumber" : "passportNumber";

      const currentPath = `vehicles.${index}.drivers.${driverIndex}.${fieldName}` as const;
      const oppositePath = `vehicles.${index}.drivers.${driverIndex}.${oppositeFieldName}` as const;

      const oppositeValue = String(formData.getValues(oppositePath) ?? "").trim();

      if (normalizedValue && oppositeValue) {
        formData.setValue(oppositePath, "", {
          shouldDirty: true,
          shouldValidate: true,
        });
      }

      const fieldsToClear = [
        `vehicles.${index}.drivers.${driverIndex}.fullName`,
        `vehicles.${index}.drivers.${driverIndex}.mobileNumber`,
        `vehicles.${index}.drivers.${driverIndex}.driverLicenseNumber`,
        `vehicles.${index}.drivers.${driverIndex}.securityClearanceId`,
        `vehicles.${index}.drivers.${driverIndex}.securityClearanceValidity`,
        `vehicles.${index}.drivers.${driverIndex}.securityClearanceFott`,
      ] as const;

      fieldsToClear.forEach((path) => {
        const value = String(formData.getValues(path) ?? "").trim();
        if (!value) return;
        formData.setValue(path, "", {
          shouldDirty: true,
          shouldValidate: true,
        });
      });

      const securityClearanceIdPath =
        `vehicles.${index}.drivers.${driverIndex}.securityClearanceId` as const;
      const securityClearanceIdValue = String(
        formData.getValues(securityClearanceIdPath) ?? "",
      ).trim();
      if (securityClearanceIdValue) {
        formData.setValue(securityClearanceIdPath, "", {
          shouldDirty: true,
          shouldValidate: true,
        });
      }

      formData.clearErrors([currentPath, oppositePath, securityClearanceIdPath]);
      resetRowScState(driverIndex);
      syncDriversToStore();
    },
    [formData, index, resetRowScState],
  );

  const handleCheckSecurityClearance = async (driverIndex: number) => {
    const passportPath = `vehicles.${index}.drivers.${driverIndex}.passportNumber` as const;
    const emiratesPath = `vehicles.${index}.drivers.${driverIndex}.emiratesIdNumber` as const;
    const driverLicensePath =
      `vehicles.${index}.drivers.${driverIndex}.driverLicenseNumber` as const;
    const duplicateScMessage = t(`${TRANSLATION_KEY}.notifications.group-member-already-added`);

    const passportNumber = String(formData.getValues(passportPath) ?? "").trim();
    const emiratesIdNumber = String(formData.getValues(emiratesPath) ?? "").trim();

    const hasValidPassport = validators.isPassportValid(passportNumber);
    const hasValidEmirates = validators.isEmiratesIdValid(emiratesIdNumber);

    formData.clearErrors([passportPath, emiratesPath, driverLicensePath]);

    if (!hasValidPassport && !hasValidEmirates) {
      const message = t("form-validation.required", { label: t(`${TRANSLATION_KEY}.document-id`) });
      formData.setError(passportPath, { type: "required", message });
      formData.setError(emiratesPath, { type: "required", message });
      setLookupError(driverIndex, message);
      return;
    }

    clearLookupError(driverIndex);
    setIsCheckingByRow((prev) => ({ ...prev, [driverIndex]: true }));

    try {
      const response = await runFieldNavigation(
        DRIVER_SECURITY_CLEARANCE_FIELD_NAVIGATION_CONTEXT,
        () =>
          runInquiry(DRIVER_SECURITY_CLEARANCE_CHECK_INQUIRY_CONTEXT, () =>
            checkGatepassSecurityClearance({
              service: GATEPASS_SERVICE,
              ...(hasValidPassport ? { passportNumber } : {}),
              ...(hasValidEmirates ? { emiratesId: emiratesIdNumber } : {}),
            }),
          ),
      );

      const currentVehicleDrivers: TruckDriverFormData[] =
        formData.getValues(`vehicles.${index}.drivers`) ?? [];

      const statusUpper = String(response.status ?? "").toUpperCase();
      const isApproved = statusUpper === "APPROVED";
      const expiryDateRaw = response.expiryDate ?? response.expirationDate;
      const notExpired =
        typeof expiryDateRaw === "string" && expiryDateRaw.trim()
          ? !Number.isNaN(new Date(expiryDateRaw).getTime()) &&
            new Date(expiryDateRaw).getTime() > Date.now()
          : true;

      if (!response.securityClearanceId || !isApproved || !notExpired) {
        const searchField = resolveScSearchField(passportNumber, emiratesIdNumber);
        setLastSearchFieldByRow((prev) => ({ ...prev, [driverIndex]: searchField }));
        setScStatusByRow((prev) => ({ ...prev, [driverIndex]: "NOT_FOUND" }));
        setScSerialByRow((prev) => ({ ...prev, [driverIndex]: "" }));
        formData.setValue(
          `vehicles.${index}.drivers.${driverIndex}.securityClearanceId` as const,
          "",
          {
            shouldDirty: true,
            shouldValidate: false,
          },
        );
        formData.setValue(
          `vehicles.${index}.drivers.${driverIndex}.securityClearanceValidity` as const,
          "",
          { shouldDirty: true, shouldValidate: false },
        );
        formData.setValue(
          `vehicles.${index}.drivers.${driverIndex}.securityClearanceFott` as const,
          "",
          { shouldDirty: true, shouldValidate: false },
        );
        clearLookupError(driverIndex);
        return;
      }

      const fullName = String(response.fullNameEnglish ?? response.fullName ?? "").trim();
      const mobileNumber = String(response.mobileNumber ?? response.phoneNumber ?? "").trim();
      const driverLicenseNumber = String(
        response.vehicleDetails?.drivingLicenseNumber ?? "",
      ).trim();
      const foundSecurityClearanceId = String(response.securityClearanceId ?? "").trim();
      const responsePassportNumber = String(response.passportNumber ?? passportNumber ?? "").trim();
      const responseEmiratesIdNumber = String(
        response.emiratesIdNumber ?? emiratesIdNumber ?? "",
      ).trim();
      const responseDriverLicenseNumber = String(
        response.vehicleDetails?.drivingLicenseNumber ?? "",
      ).trim();

      const isDuplicateWithDriverInSameVehicle = currentVehicleDrivers.some((driver, idx) => {
        if (idx === driverIndex) {
          return false;
        }

        const driverScId = String(driver.securityClearanceId ?? "").trim();
        const driverPassport = String(driver.passportNumber ?? "").trim();
        const driverEmiratesId = String(driver.emiratesIdNumber ?? "").trim();

        if (foundSecurityClearanceId && driverScId) {
          return driverScId === foundSecurityClearanceId;
        }

        return Boolean(
          (responsePassportNumber && driverPassport && driverPassport === responsePassportNumber) ||
            (responseEmiratesIdNumber &&
              driverEmiratesId &&
              driverEmiratesId === responseEmiratesIdNumber),
        );
      });

      if (isDuplicateWithDriverInSameVehicle) {
        setLookupError(driverIndex, duplicateScMessage);
        setScStatusByRow((prev) => ({ ...prev, [driverIndex]: "" }));
        setScSerialByRow((prev) => ({ ...prev, [driverIndex]: "" }));
        return;
      }

      const isDuplicateLicenseNumberInSameVehicle =
        Boolean(responseDriverLicenseNumber) &&
        currentVehicleDrivers.some((driver, idx) => {
          if (idx === driverIndex) {
            return false;
          }

          return (
            String(driver.driverLicenseNumber ?? "")
              .trim()
              .toLowerCase() === responseDriverLicenseNumber.toLowerCase()
          );
        });

      const duplicateDriverLicenseMessage = t(`${TRANSLATION_KEY}.duplicate-driver-license-number`);

      if (fullName) {
        formData.setValue(`vehicles.${index}.drivers.${driverIndex}.fullName` as const, fullName, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }

      if (responsePassportNumber) {
        formData.setValue(
          `vehicles.${index}.drivers.${driverIndex}.passportNumber` as const,
          responsePassportNumber,
          {
            shouldDirty: true,
            shouldValidate: true,
          },
        );
      }

      if (responseEmiratesIdNumber) {
        formData.setValue(
          `vehicles.${index}.drivers.${driverIndex}.emiratesIdNumber` as const,
          responseEmiratesIdNumber,
          {
            shouldDirty: true,
            shouldValidate: true,
          },
        );
      }

      if (mobileNumber) {
        formData.setValue(
          `vehicles.${index}.drivers.${driverIndex}.mobileNumber` as const,
          mobileNumber,
          {
            shouldDirty: true,
            shouldValidate: true,
          },
        );
      }

      if (driverLicenseNumber) {
        formData.setValue(
          `vehicles.${index}.drivers.${driverIndex}.driverLicenseNumber` as const,
          driverLicenseNumber,
          {
            shouldDirty: true,
            shouldValidate: true,
          },
        );
      }

      formData.setValue(
        `vehicles.${index}.drivers.${driverIndex}.securityClearanceId` as const,
        foundSecurityClearanceId,
        {
          shouldDirty: true,
          shouldValidate: false,
        },
      );
      const formattedScValidity =
        typeof expiryDateRaw === "string" && expiryDateRaw.trim()
          ? (() => {
              const parsed = new Date(expiryDateRaw);
              if (Number.isNaN(parsed.getTime())) return "";
              const day = String(parsed.getDate()).padStart(2, "0");
              const month = String(parsed.getMonth() + 1).padStart(2, "0");
              const year = parsed.getFullYear();
              return `${day}/${month}/${year}`;
            })()
          : "";
      formData.setValue(
        `vehicles.${index}.drivers.${driverIndex}.securityClearanceValidity` as const,
        formattedScValidity,
        {
          shouldDirty: true,
          shouldValidate: false,
        },
      );
      formData.setValue(
        `vehicles.${index}.drivers.${driverIndex}.securityClearanceFott` as const,
        response.fott === true ? t("yes") : t("no"),
        {
          shouldDirty: true,
          shouldValidate: false,
        },
      );
      setScStatusByRow((prev) => ({
        ...prev,
        [driverIndex]: String(response.status ?? "APPROVED"),
      }));
      setScSerialByRow((prev) => ({
        ...prev,
        [driverIndex]: String(response.serialNo ?? response.serialNumber ?? ""),
      }));

      setLastSearchFieldByRow((prev) => ({
        ...prev,
        [driverIndex]: resolveScSearchField(passportNumber, emiratesIdNumber),
      }));

      formData.clearErrors(`vehicles.${index}.drivers.${driverIndex}.securityClearanceId`);
      if (isDuplicateLicenseNumberInSameVehicle) {
        formData.setError(driverLicensePath, {
          type: "manual",
          message: duplicateDriverLicenseMessage,
        });
      }
      clearLookupError(driverIndex);
      syncDriversToStore();
    } catch (error) {
      const detail = extractScErrorDetail(error) || t("form-validation.invalid");
      setScStatusByRow((prev) => ({ ...prev, [driverIndex]: "" }));
      setScSerialByRow((prev) => ({ ...prev, [driverIndex]: "" }));
      setLookupError(driverIndex, detail);
    } finally {
      setIsCheckingByRow((prev) => ({ ...prev, [driverIndex]: false }));
    }
  };

  const canRunManualScCheck = (driverIndex: number) => {
    const passport = String(
      formData.getValues(`vehicles.${index}.drivers.${driverIndex}.passportNumber`) ?? "",
    );
    const emirates = String(
      formData.getValues(`vehicles.${index}.drivers.${driverIndex}.emiratesIdNumber`) ?? "",
    );

    return (
      !isCheckingByRow[driverIndex] &&
      (validators.isPassportValid(passport) || validators.isEmiratesIdValid(emirates))
    );
  };

  const renderCheckMessage = (driverIndex: number) => {
    const status = String(scStatusByRow[driverIndex] ?? "").trim();
    const serial = String(scSerialByRow[driverIndex] ?? "").trim();
    const securityClearanceId = String(
      formData.getValues(`vehicles.${index}.drivers.${driverIndex}.securityClearanceId`) ?? "",
    ).trim();

    const handleSerialClick = () => {
      if (!securityClearanceId) return;
      const url = `/${paths.portSecurity.index}/${paths.portSecurity.securityClearanceRequestPreview}/${securityClearanceId}`;
      window.open(url, "_blank");
    };

    return (
      <SecurityClearanceStatusMessage
        isChecking={Boolean(isCheckingByRow[driverIndex])}
        status={status}
        serial={serial}
        errorMessage={lookupErrorByIdx[driverIndex]}
        lastSearchField={lastSearchFieldByRow[driverIndex] ?? null}
        onSerialClick={handleSerialClick}
        translationKey={TRANSLATION_KEY}
      />
    );
  };

  const isAutoPopulatedDriver = (driverIndex: number) =>
    driverIndex === 0 && (autoPopulateFirstDriverAcrossVehicles || index === 0);

  return (
    <FormControl
      fullWidth
      className="col-span-full mt-2 overflow-hidden rounded border border-gray-200"
    >
      <div className="flex items-center justify-between border-b border-gray-200 bg-[#F1F2F4] px-4 py-2">
        <h4 className="text-sm font-semibold">
          {t(`${TRANSLATION_KEY}.driver-details`, {
            defaultValue: "Driver Details",
          })}
        </h4>
        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          className="normal-case"
          disabled={disableAddDriver && fields.length >= 1}
          label={
            <span className="normal-case text-xs font-medium">
              {t(`${TRANSLATION_KEY}.add-driver`, { defaultValue: "Add New Driver" })}
            </span>
          }
          testId={`truck-driver-add-${index}`}
          onClick={() => {
            append({
              driverLicenseNumber: "",
              fullName: "",
              passportNumber: "",
              emiratesIdNumber: "",
              mobileNumber: "",
              securityClearanceId: "",
              securityClearanceValidity: "",
              securityClearanceFott: "",
            } satisfies TruckDriverFormData);
            syncDriversToStore();
          }}
        />
      </div>

      <div className="space-y-4 p-4">
        {fields.map((field, driverIndex) => {
          const isAutoFilled = isAutoPopulatedDriver(driverIndex);
          const driverErrors = ((
            (formData.formState.errors?.vehicles as unknown[])?.[index] as
              | {
                  drivers?: unknown[];
                }
              | undefined
          )?.drivers?.[driverIndex] ?? {}) as {
            passportNumber?: { message?: string };
            emiratesIdNumber?: { message?: string };
            securityClearanceId?: { message?: string };
            securityClearanceValidity?: { message?: string };
          };
          const driverScValidityError = driverErrors.securityClearanceValidity?.message;
          const scValidityErrorTestId = `truck-driver-scValidityError-${index}-${driverIndex}`;
          // Match GroupMembers: show entry header for additional rows (idx > 0 || length > 1).
          // On truck, also treat the first driver on an additional vehicle like group member 2+.
          const titleVisible = index > 0 || driverIndex > 0 || fields.length > 1;
          const showEntryDivider = driverIndex > 0 || index > 0;

          return (
            <div key={field.id} className="space-y-4">
              {titleVisible && (
                <>
                  {showEntryDivider && <FormDivider />}
                  <div className="col-span-2 pt-4">
                    <div className="flex justify-between">
                      <Typography variant="body2">
                        {t(`${TRANSLATION_KEY}.driver-details-with-number`, {
                          number: driverIndex + 1,
                        })}
                      </Typography>
                      {fields.length > 1 && driverIndex > 0 && (
                        <RemoveButton
                          testId={`truck-driver-remove-${index}-${driverIndex}`}
                          startIcon={<CloseIcon />}
                          type="button"
                          disableElevation
                          variant="contained"
                          label={<span className="normal-case font-medium">Remove</span>}
                          onClick={() => {
                            remove(driverIndex);
                            remapStateAfterRemoval(driverIndex);
                            syncDriversToStore();
                          }}
                        />
                      )}
                    </div>
                    {driverScValidityError &&
                      renderScValidityError(driverScValidityError, scValidityErrorTestId)}
                  </div>
                </>
              )}
              {!titleVisible && driverScValidityError && (
                <div className="col-span-2 pt-4">
                  {renderScValidityError(driverScValidityError, scValidityErrorTestId)}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormItem<TruckGatepassFormData>
                  name={`vehicles.${index}.drivers.${driverIndex}.passportNumber`}
                  type="text"
                  label={t(`${TRANSLATION_KEY}.passport-number`)}
                  fullWidth
                  disabled={isAutoFilled}
                  testId={`truckGatepass-driverPassport-${index}-${driverIndex}`}
                  onFieldChange={(_: string, value: unknown) => {
                    handleDriverIdChange(driverIndex, "passportNumber", value);
                  }}
                  enforceUppercase
                />

                <div>
                  <CustomFormLabel
                    data-testid={`truckGatepass-driverEmiratesLabel-${index}-${driverIndex}`}
                    sx={{ mb: 1, display: "block" }}
                  >
                    {t(`${TRANSLATION_KEY}.emirates-id-number`)}
                  </CustomFormLabel>
                  <Controller
                    name={`vehicles.${index}.drivers.${driverIndex}.emiratesIdNumber`}
                    control={formData.control}
                    render={({ field: inputField }) => (
                      <Input
                        {...inputField}
                        fullWidth
                        disabled={isAutoFilled}
                        testId={`truckGatepass-driverEmirates-${index}-${driverIndex}`}
                        placeholder={t(`${TRANSLATION_KEY}.emirates-id-number`)}
                        error={Boolean(driverErrors.emiratesIdNumber)}
                        onChange={(e) => {
                          const value = formatEmiratesIdValue(e.target.value);
                          inputField.onChange(value);
                          handleDriverIdChange(driverIndex, "emiratesIdNumber", value);
                        }}
                      />
                    )}
                  />
                  {driverErrors.emiratesIdNumber?.message && (
                    <FormHelperText error>
                      {String(driverErrors.emiratesIdNumber.message)}
                    </FormHelperText>
                  )}
                </div>

                {!isAutoFilled && (
                  <div className="md:col-span-2">
                    {renderCheckMessage(driverIndex)}
                    <Button
                      testId={`truck-driver-checkSc-${index}-${driverIndex}`}
                      type="button"
                      variant="outlined"
                      color="primary"
                      className="bg-white"
                      sx={{ textTransform: "none" }}
                      disabled={!canRunManualScCheck(driverIndex)}
                      onClick={() => {
                        void handleCheckSecurityClearance(driverIndex);
                      }}
                      label={t(`${TRANSLATION_KEY}.check-security-clearance`)}
                    />
                    {driverErrors.securityClearanceId?.message && (
                      <FormHelperText error>
                        {String(driverErrors.securityClearanceId.message)}
                      </FormHelperText>
                    )}
                  </div>
                )}
                <div className="md:col-span-2 flex flex-col gap-6 pb-7 md:flex-row">
                  <div className="flex min-w-0 flex-1 flex-col">
                    <CustomFormLabel sx={{ mb: 1 }} disabled>
                      {t("gatepass.security-clearance-validity", {
                        defaultValue: "Security clearance validity",
                      })}
                    </CustomFormLabel>
                    <div className="mt-auto w-full">
                      <Controller
                        name={`vehicles.${index}.drivers.${driverIndex}.securityClearanceValidity`}
                        control={formData.control}
                        render={({ field: inputField }) => (
                          <Input
                            {...inputField}
                            fullWidth
                            disabled
                            testId={`truckGatepass-driverSecurityClearanceValidity-${index}-${driverIndex}`}
                            placeholder="-"
                          />
                        )}
                      />
                    </div>
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col">
                    <CustomFormLabel sx={{ mb: 1 }} disabled>
                      {t("gatepass.fott", { defaultValue: "FOTT" })}
                    </CustomFormLabel>
                    <div className="mt-auto w-full">
                      <Controller
                        name={`vehicles.${index}.drivers.${driverIndex}.securityClearanceFott`}
                        control={formData.control}
                        render={({ field: inputField }) => (
                          <RadioGroup
                            row
                            {...inputField}
                            value={inputField.value ?? ""}
                            onChange={(e) => inputField.onChange(e.target.value)}
                            sx={{
                              "display": "flex",
                              "flexDirection": "row",
                              "flexWrap": "wrap",
                              "columnGap": 2,
                              "rowGap": 0.5,
                              ".MuiFormControlLabel-root": {
                                marginRight: 0,
                              },
                            }}
                          >
                            {fottDisplayOptions.map((option) => (
                              <FormControlLabel
                                key={option.value}
                                value={option.value}
                                control={<Radio />}
                                label={option.label}
                                disabled
                              />
                            ))}
                          </RadioGroup>
                        )}
                      />
                    </div>
                  </div>
                </div>

                <FormItem<TruckGatepassFormData>
                  required
                  name={`vehicles.${index}.drivers.${driverIndex}.driverLicenseNumber`}
                  type="text"
                  maxChars={50}
                  label={t("security-clearance.driving-license-number")}
                  testId={`truckGatepass-driverLicenseNumber-${index}-${driverIndex}`}
                  onFieldChange={syncDriversToStore}
                />
                <FormItem<TruckGatepassFormData>
                  required
                  name={`vehicles.${index}.drivers.${driverIndex}.fullName`}
                  type="text"
                  label={t(`${TRANSLATION_KEY}.fullName-en`)}
                  testId={`truckGatepass-driverFullName-${index}-${driverIndex}`}
                  disabled
                  onFieldChange={syncDriversToStore}
                />
                <FormItem<TruckGatepassFormData>
                  required
                  name={`vehicles.${index}.drivers.${driverIndex}.mobileNumber`}
                  type="text"
                  maxChars={50}
                  label={t(`${TRANSLATION_KEY}.phone-number`)}
                  testId={`truckGatepass-driverMobile-${index}-${driverIndex}`}
                  disabled={isAutoFilled}
                  onFieldChange={syncDriversToStore}
                />
              </div>
            </div>
          );
        })}
      </div>
    </FormControl>
  );
};

export default DriverDetails;
